import { Box, Flex, FormControl, FormErrorMessage, FormLabel, IconButton, Input, InputGroup, InputRightElement, Radio, RadioGroup, Tag } from '@chakra-ui/react';
import React, { MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FaAngleDown } from 'react-icons/fa6';

import { Dropdown } from './dropdown';
import { Txt } from './text';
import { FieldComponentProps } from './types';

type VisitFrequencyProps = FieldComponentProps<string> & {
  disabledZeroOption?: boolean;
};

export const VisitFrequencySelectField = ({ 
  value, 
  onChange, 
  id, 
  error, 
  required, 
  disabled, 
  label,
  helperText,
  disabledZeroOption,
  readOnly = false,
}: VisitFrequencyProps) => {
  const [frequency, setFrequency] = useState<EntityBase>(disabledZeroOption ? FrequencyOptions[1] : FrequencyOptions[0]);
  const [days, setDays] = useState<number[][]>([[], [], [], []]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!value || Number.isNaN(Number(value))) {
      setFrequency(FrequencyOptions[0]);
      setDays([[], [], [], []]); 
      return;
    }
    const [frequency, days] = getDays(value);
    setFrequency(frequency);
    setDays(days);
  }, [value]);

  const changeDays = useCallback((value: number[][]) => {
    setDays(value);
    onChange(value.map((w, i) => w.length ? `${i + 1}${w.join('')}` : '').join(''));
  }, [onChange]);


  const onSetFrequency = useCallback((frequency: EntityBase) => {
    setFrequency(frequency);
    switch (frequency.id) {
      case 0: changeDays([[], [], [], []]); break;
      case 1: changeDays([[1], [], [], []]); break;
      case 2: changeDays([[1], [], [1], []]); break;
      case 4: changeDays([[1], [1], [1], [1]]); break;
      case 8: changeDays([[1, 2], [1, 2], [1, 2], [1, 2]]); break;
      case 12: changeDays([[1,2,3], [1,2,3], [1,2,3], [1,2,3]]); break;
    }
  }, [changeDays]);

  const onSelectDay = useCallback((week: number) => (value: number) => {
    if (frequency.id === 0) {
      changeDays([[], [], [], []]);
      return;
    }
    if (frequency.id === 1) {
      const days: number[][] = [[], [], [], []];
      days[week] = [value];
      changeDays(days);
      return;
    }
    if (frequency.id === 2) {
      if(days[week].length) {
        days[week] = [value];
        changeDays([...days]);
        return;
      }
      switch (week) {
        case 0: changeDays([[value], [], [1], []]); break;
        case 1: changeDays([[], [value], [], [1]]); break;
        case 2: changeDays([[1], [], [value], []]); break;
        case 3: changeDays([[], [1], [], [value]]); break;
      }
      return;
    }
    if (frequency.id === 4) {
      days[week] = [value];
      changeDays([...days]);
      return;
    }
    if (frequency.id === 8 || frequency.id === 12) {
     return;
    }
  }, [frequency.id, days, changeDays]);

  const onChangeWeek = useCallback((week: number) => (value: number[]) => {
    days[week] = value;
    changeDays([...days]);
  }, [days, changeDays]);

  const swapMode = Number(frequency.id || 1) > 4;

  const onFrequencyChange = useCallback((nextValue: string) => {
    if (disabledZeroOption && nextValue === '0') {
      return;
    }
    onSetFrequency(FrequencyOptions.find(({ id }) => id.toString() === nextValue));
  }, [onSetFrequency, disabledZeroOption]);

  const handleMouseDown = () => {
    if (readOnly) {return;}
    !disabled && setOpen(!open);
  };

  const handleClickOutside = () => {
    document.removeEventListener('mousedown', handleClickOutside);
    setOpen(false);
  };

  const handleMouseLeave = () => {
    document.addEventListener('mousedown', handleClickOutside);
  };

  const handleMouseEnter = () => {
    document.removeEventListener('mousedown', handleClickOutside);
  };

  const onFocus = () => {
    if (readOnly) {return;}
    !disabled && setOpen(true);
  };

  const fieldRef = useRef<HTMLDivElement>(null);

  if (disabled) {
    return <InputGroup>
      <FormControl 
        variant="floating" 
        id={id}
      >
        <Input 
          disabled
          placeholder={label}
        />
      </FormControl>
    </InputGroup>;
  }

  return <InputGroup>
    <FormControl 
      variant="floating" 
      id={id} 
      isRequired={required}
      isInvalid={error}
      isDisabled={disabled}
      ref={fieldRef}
      onMouseLeave={handleMouseLeave}
      onMouseEnter={handleMouseEnter}
    >
      <InputRightElement pr={0}>
        <IconButton icon={<FaAngleDown />} aria-label="clear" 
          backgroundColor="transparent" 
          borderRadius="50%"
          size="xs"
          onClick={handleMouseDown}
          transform={open ?  'rotate(180deg)' : 'rotate(0deg)'}
        />
      </InputRightElement>
      <Dropdown 
        fieldRef={fieldRef}
        open={open}
        setOpen={setOpen}
        autoWidth
      >
        <Box userSelect='none' p={4}>
          <Txt >Частота посещений в мес.</Txt>
          <RadioGroup onChange={onFrequencyChange} value={frequency.id.toString()} display='flex' gap={2}>
            {FrequencyOptions.map(({ name, id }) => 
              <Radio 
                key={id.toString()} 
                value={id.toString()} 
                disabled={disabledZeroOption && id === 0}
              >{name}</Radio>
            )}
          </RadioGroup>
          <Box marginTop={4}>
            <Txt>Дни посещений:</Txt>
            <Box 
              borderColor="gray.500" 
              borderWidth={1} borderRadius={8} 
              marginTop={2} padding={1} gap={1}
              flexDirection="column"
              alignItems="center"
              display='flex'
              flexDir='column'
              minWidth={390}
            >
              <Box display="flex" flexDirection="row" alignItems="center">
                <Box 
                  display='flex' justifyContent='center'
                  backgroundColor={days[0].length ? 'brand.100' : 'brand.500'} 
                  color='white'  minWidth="64px" height="24px"
                  borderRadius={12} alignItems="center"
                  marginRight={4}
                >1 нед</Box>
                <Week 
                  selectedDays={days[0]} 
                  swapMode={swapMode} 
                  isActive={!!days[0].length} 
                  onSelect={onSelectDay(0)}
                  onChange={onChangeWeek(0)}
                />
              </Box>
              <Box display="flex" flexDirection="row" alignItems="center">
                <Box 
                  display='flex' justifyContent='center'
                  backgroundColor={days[0].length ? 'brand.100' : 'brand.500'} 
                  color='white'  minWidth="64px" height="24px"
                  borderRadius={12} alignItems="center"
                  marginRight={4}
                >2 нед</Box>
                <Week 
                  selectedDays={days[1]} 
                  swapMode={swapMode} 
                  isActive={!!days[1].length} 
                  onSelect={onSelectDay(1)} 
                  onChange={onChangeWeek(1)}
                />
              </Box>
              <Box display="flex" flexDirection="row" alignItems="center">
                <Box 
                  display='flex' justifyContent='center'
                  backgroundColor={days[0].length ? 'brand.100' : 'brand.500'} 
                  color='white'  minWidth="64px" height="24px"
                  borderRadius={12} alignItems="center"
                  marginRight={4}
                >3 нед</Box>
                <Week
                  selectedDays={days[2]} 
                  swapMode={swapMode} 
                  isActive={!!days[2].length} 
                  onSelect={onSelectDay(2)} 
                  onChange={onChangeWeek(2)}
                />
              </Box>
              <Box display="flex" flexDirection="row" alignItems="center">
                <Box 
                  display='flex' justifyContent='center'
                  backgroundColor={days[0].length ? 'brand.100' : 'brand.500'} 
                  color='white'  minWidth="64px" height="24px"
                  borderRadius={12} alignItems="center"
                  marginRight={4}
                >4 нед</Box>
                <Week 
                  selectedDays={days[3]} 
                  swapMode={swapMode} 
                  isActive={!!days[3].length} 
                  onSelect={onSelectDay(3)} 
                  onChange={onChangeWeek(3)}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </Dropdown>
      <Box 
        borderRadius='var(--input-border-radius)'
        paddingInlineStart='var(--input-padding)'
        paddingInlineEnd='var(--input-padding)'
        paddingY='1'
        border='1px solid'
        w='100%'
        minH='var(--input-height)'
        display='flex'
        flexWrap='wrap'
        alignItems='center'
        gap={1}
        onClick={() => !readOnly && setOpen(!open)}
      >
        {!value && <Txt color='gray.500'>Не посещается</Txt>}
        {value && <VisitFrequencyView days={days} />}
      </Box>
      <Input 
        position="absolute"
        opacity={0}
        placeholder={label}
        value={value || 'Не посещается'}
        contentEditable={false}
        onFocus={onFocus}
        onClick={() => !readOnly && setOpen(true)}
        isReadOnly
      />
      <FormLabel textColor="dark.300">{label}</FormLabel>
      {error && <FormErrorMessage>{helperText}</FormErrorMessage>}
    </FormControl>
  </InputGroup>;
};

const FrequencyOptions = [
  { id: 0, name: '0'},
  { id: 1, name: '1'},
  { id: 2, name: '2'},
  { id: 4, name: '4'},
  { id: 8, name: '8'},
  { id: 12, name: '12'}
];

const Week = ({selectedDays, swapMode, isActive, onSelect, onChange}: { 
  selectedDays: number[], 
  swapMode: boolean,
  isActive: boolean, 
  onSelect(value: number): void 
  onChange(week: number[]): void
}) => {

  const onSlide = (oldValue: number, newValue: number) => {
    const week = selectedDays.filter((day) => day !== oldValue);
    if (week.includes(newValue)) { 
      
      if (newValue > 1 && !week.includes(newValue - 1)) {
        onChange([
          ...week,
          newValue - 1
        ]);
        return;
      }
      if (newValue < 7 && !week.includes(newValue + 1)) {
        onChange([
          ...week,
          newValue + 1
        ]);
        return;
      }

      if (newValue > 2 && !week.includes(newValue - 2)) {
        onChange([
          ...week,
          newValue - 2
        ]);
        return;
      }
      if (newValue < 6 && !week.includes(newValue + 2)) {
        onChange([
          ...week,
          newValue + 2
        ]);
        return;
      }
    }
    onChange([
      ...week,
      newValue 
    ]);
  };

  return <Box 
    display="flex" 
    flexDirection="row" 
    borderRadius={8} 
    backgroundColor={isActive ? 'light.300' : 'gray.300'} 
    padding='2px'
    gap="2px"
    position='relative'
  >
    {weekDays.map(({ id, label }) => <Box key={id} 
      onClick={() => !swapMode && onSelect(id)}
    >
      <Box 
        padding='2px'
        backgroundColor="transparent"
        borderRadius={6} width="40px" justifyContent="center"
        cursor='pointer'
      >
        <Txt style={{ textAlign: 'center', color: 'black' }} >{label}</Txt>
      </Box>
    </Box>)}
    {selectedDays.map((position, index) => <Slider key={index} position={position} onChange={onSlide} week={selectedDays} />)}
  </Box>;
};

const Slider = ({ position, onChange }: {
  position: number,
  week: number[],
  onChange(oldValue: number, value: number): void
}) => {
  const [isMoved, setIsMoved] = useState(false);
  const [offset, setOffset] = useState<number>(0);
  const startX = useRef(0);

  const x = useMemo(() => {
    return 42 * (position - 1) + 2;
  }, [position]);

  const onMoveStart = (e: MouseEvent<HTMLDivElement>) => {
    startX.current = e.clientX;
    setIsMoved(true);
  };

  const onMove = (e: MouseEvent<HTMLDivElement>) => {
    if (!isMoved) {return;}
    const newOffset = e.clientX - startX.current;
    if ((newOffset + x) < 0) {
      return;
    }
    
    if ((newOffset + x) > 254) {
      setOffset(254 - x);
      return;
    }
    
    setOffset(e.clientX - startX.current);
  };

  const onMoveEnd = () => {
    if (!isMoved) {return;}
    const newPosition = Math.round((offset - 2) / 41) + position;

    onChange(position, newPosition);
    setOffset(0);
    setIsMoved(false);
  };

  const label = useMemo(() => {
    const newPosition = Math.round((offset - 2) / 41) + position;
    
    return weekDays[(newPosition > 7 ? 7 : newPosition) - 1].label;
  }, [position, offset]);

  return (<Box position="absolute"
    left={x + offset + 'px'} top={1}
    width="40px" borderRadius={6}
    justifyContent="center"
    backgroundColor={isMoved ? 'brand.100' : 'brand.500'}
    onMouseDown={onMoveStart}
    onMouseMove={onMove}
    onMouseUp={onMoveEnd}
    onMouseLeave={onMoveEnd}
    zIndex={isMoved ? 1 : 0}
    cursor='pointer'
  >
    <Txt style={{ textAlign: 'center', color: 'white' }} >
      {label}
    </Txt>
  </Box>);
};

const weekDays = [
  { id: 1, label: 'ПН' },
  { id: 2, label: 'ВТ' },
  { id: 3, label: 'СР' },
  { id: 4, label: 'ЧТ' },
  { id: 5, label: 'ПТ' },
  { id: 6, label: 'СБ' },
  { id: 7, label: 'ВС' },
];

export const VisitFrequencyView = ({ days }: {
  days: number[][]
}) => {
  return (<Flex alignItems='center' gap={1} >
    {days.map((week, index) => {
      if(week.length === 0) {return null;}
      return <React.Fragment key={index}>
        <Flex 
          justify='center' 
          alignItems='center' 
          backgroundColor='brand.100' 
          borderRadius='50%' 
          width='18px' height='18px'
          fontSize='smaller'
        >{index + 1}</Flex>
        {week.map((day, index) => <Tag key={index} fontSize='small' p={1} >{weekDays[day-1].label}</Tag>)}
      </React.Fragment>;
    })}
  </Flex>);
};


export const getDays = (value: string): [EntityBase, number[][]] => {
  const days: number[][] = [[], [], [], []];
  if (!value) { 
    return [FrequencyOptions[0], days]; 
  }

  switch(value.length) {
    case 2: { // 1 day per month
      const [week, day] = value.split('');
      days[Number(week) - 1] = [Number(day)];
      return [FrequencyOptions[1], days];
    }
    case 4: { // 2 days per month
      const [w1, w1d, w2, w2d] = value.split('');
      days[Number(w1) - 1] = [Number(w1d)];
      days[Number(w2) - 1] = [Number(w2d)];
      return [FrequencyOptions[2], days];
    }
    case 8: { // 4 days per month
      const [_, w1d, __, w2d, ___, w3d, ____, w4d] = value.split('');
      return [FrequencyOptions[3], [[Number(w1d)], [Number(w2d)], [Number(w3d)],  [Number(w4d)]]]; 
    }
    case 12: { // 8 days per month
      const [w1, w1d1, w1d2, w2, w2d1, w2d2, w3, w3d1, w3d2, w4, w4d1, w4d2] = value.split('');
      return [FrequencyOptions[4], [
        [Number(w1d1), Number(w1d2)], 
        [Number(w2d1), Number(w2d2)], 
        [Number(w3d1), Number(w3d2)],  
        [Number(w4d1), Number(w4d2)]
      ]]; 
    }
    case 16: { // 2 days per month
      const [w1, w1d1, w1d2, w1d3, w2, w2d1, w2d2, w2d3, w3, w3d1, w3d2, w3d3, w4, w4d1, w4d2, w4d3] = value.split('');
      return [FrequencyOptions[5], [
        [Number(w1d1), Number(w1d2), Number(w1d3)], 
        [Number(w2d1), Number(w2d2), Number(w2d3)], 
        [Number(w3d1), Number(w3d2), Number(w3d3)],  
        [Number(w4d1), Number(w4d2), Number(w4d3)]
      ]]; 
    }
    default: {
      return [FrequencyOptions[0], [[], [], [], []]]; 
    }
  }
};